import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Zap, Search, Users, Sparkles, Signal, Plus, ArrowRight,
    RefreshCw, CheckCircle, Calendar
} from 'lucide-react';
import Navbaar from '../../components/Navbar/Navbaar';
import Sidebar from '../../components/Navbar/SideNavbar';
import { fetchCreditHistory, createCreditCheckoutSession, fetchAllCreditProducts } from '../../redux/PaymentSlice/paymentSlice';

// ─── helpers ──────────────────────────────────────────────────────────────────

const actionToType = (action, amount) => {
    if (amount > 0) return 'Credits added';
    const a = String(action || '').toLowerCase();
    if (/recruit|contact|lookup|prospeo|contactout/.test(a)) return 'Recruiter search';
    if (/ai|chat|assistant|gpt|deepseek|recompute|match/.test(a)) return 'AI Assistant';
    if (/signal|hiring|hsignal/.test(a)) return 'Hiring signal';
    if (/job/.test(a)) return 'Job search';
    return 'Other';
};

const TYPE_META = {
    'Recruiter search': { color: '#E11D48', bg: '#FEF2F2', Icon: Users },
    'AI Assistant':     { color: '#7C3AED', bg: '#F5F3FF', Icon: Sparkles },
    'Hiring signal':    { color: '#F59E0B', bg: '#FFFBEB', Icon: Signal },
    'Credits added':    { color: '#10B981', bg: '#ECFDF5', Icon: Plus },
    'Job search':       { color: '#3F57E0', bg: '#EEF1FE', Icon: Search },
    'Other':            { color: '#94A3B8', bg: '#F4F5F8', Icon: Zap },
};

// No 'AI Assistant' chip: the assistant is free, so it never writes ledger rows.
const FILTER_TABS = ['All', 'Recruiter search', 'Hiring signal', 'Job search', 'Credits added'];

const COST_REFERENCE = [
    { label: 'Fetch ATS jobs',      cost: 'Free' },
    // JSearch fetch retired from the Fetch More Jobs modal — restore if re-enabled:
    // { label: 'Fetch jobs',          cost: '1 credit per job' },
    { label: 'Recruiter lookup',    cost: '1 credit per contact' },
    // Free since hiring signals moved to the US public-company data (SEC
    // filings, earnings calls and WARN notices), which is stored and read
    // locally. The old 2-credits-per-signal price was TheirStack's per-company
    // billing, and that provider is no longer in this path.
    { label: 'Hiring signal',       cost: 'Free' },
    { label: 'AI Assistant',        cost: 'Free' },
];

function formatDateLabel(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function formatDateTime(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    const date = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${date}, ${time}`;
}

function groupByDay(items) {
    const map = new Map();
    items.forEach(item => {
        const key = formatDateLabel(item.createdAt);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(item);
    });
    return Array.from(map.entries());
}

function getDescription(item) {
    if (item.meta?.description) return item.meta.description;
    if (item.meta?.companyName) return `Lookup for ${item.meta.companyName}`;
    if (item.meta?.jobTitle) return `AI for "${item.meta.jobTitle}"`;
    if (item.meta?.creditsAdded) return `Top-up · ${item.meta.creditsAdded} credits added`;
    const a = String(item.action || '').replace(/_/g, ' ');
    return a.charAt(0).toUpperCase() + a.slice(1);
}

function getCount(item) {
    if (item.meta?.contactsChecked != null) return `${item.meta.contactsChecked} contact${item.meta.contactsChecked !== 1 ? 's' : ''} checked`;
    if (item.meta?.tokens) return `${item.meta.tokens} tokens`;
    if (item.meta?.jobsCount) return `${item.meta.jobsCount} jobs`;
    if (item.amount > 0) return 'Auto-renewal';
    const abs = Math.abs(item.amount);
    return `${abs} credit${abs !== 1 ? 's' : ''}`;
}

function getStartOfMonth() {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
}

function getDaysIntoMonth() {
    return new Date().getDate();
}

const FALLBACK_PACKS = [
    { id: null, label: '100 credits',   price: '$9',   per: '$0.09 ea',  credits: 100,  best: false },
    { id: null, label: '300 credits',   price: '$24',  per: '$0.08 ea',  credits: 300,  best: true  },
    { id: null, label: '700 credits',   price: '$49',  per: '$0.07 ea',  credits: 700,  best: false },
];

// ─── main page ────────────────────────────────────────────────────────────────

const CreditsPage = () => {
    const dispatch = useDispatch();
    const user = useSelector(s => s.auth.user);
    const {
        creditHistory, creditHistoryLoading, creditHistoryError,
        creditHistoryHasMore, creditHistoryNextCursor,
        creditProducts, checkoutLoading,
    } = useSelector(s => s.payments);

    const [activeFilter, setActiveFilter]   = useState('All');
    const [searchQuery, setSearchQuery]     = useState('');
    const [timeRange, setTimeRange]         = useState('30d');
    const [selectedPack, setSelectedPack]   = useState(1);
    const [checkoutErrMsg, setCheckoutErrMsg] = useState('');

    const creditsBalance = user?.creditsBalance ?? 0;

    useEffect(() => {
        dispatch(fetchCreditHistory({ limit: 100 }));
        dispatch(fetchAllCreditProducts());
    }, [dispatch]);

    // ── derived data ──────────────────────────────────────────────────────────

    const rangeFilteredHistory = useMemo(() => {
        if (!Array.isArray(creditHistory)) return [];
        if (timeRange === 'All') return creditHistory;
        const days = timeRange === '1d' ? 1 : timeRange === '7d' ? 7 : 30;
        const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
        return creditHistory.filter(i => new Date(i.createdAt) >= cutoff);
    }, [creditHistory, timeRange]);

    const filteredHistory = useMemo(() => {
        let items = rangeFilteredHistory;
        if (activeFilter !== 'All') {
            items = items.filter(i => actionToType(i.action, i.amount) === activeFilter);
        }
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            items = items.filter(i =>
                getDescription(i).toLowerCase().includes(q) ||
                String(i.action || '').toLowerCase().includes(q) ||
                String(i.source || '').toLowerCase().includes(q)
            );
        }
        return items;
    }, [rangeFilteredHistory, activeFilter, searchQuery]);

    const grouped = useMemo(() => groupByDay(filteredHistory), [filteredHistory]);

    // Month stats
    const thisMonthItems = useMemo(() => {
        const startOfMonth = getStartOfMonth();
        return (creditHistory || []).filter(i => new Date(i.createdAt) >= startOfMonth);
    }, [creditHistory]);

    const usedThisMonth = useMemo(() =>
        thisMonthItems.filter(i => i.amount < 0).reduce((s, i) => s + Math.abs(i.amount), 0)
    , [thisMonthItems]);

    // Per-type spend for the SELECTED time range (7d/30d/All) — matches what the
    // activity log below is showing, unlike the month-scoped headline stats.
    const byType = useMemo(() => {
        const counts = {};
        rangeFilteredHistory.filter(i => i.amount < 0).forEach(i => {
            let t = actionToType(i.action, i.amount);
            // Split job-search spend by flow via the API's source field. The retired
            // JSearch flows ("By My Preferences" / "By Keyword Search") are folded into
            // a single "Job search" bucket now that they're hidden from the fetch modal.
            if (t === 'Job search') {
                const src = i.source || '';
                t = (src === 'Fetch Latest ATS Jobs' || src === 'Feed Auto-Refresh') ? src : 'Job search';
            }
            counts[t] = (counts[t] || 0) + Math.abs(i.amount);
        });
        return counts;
    }, [rangeFilteredHistory]);

    const avgPerDay = useMemo(() => {
        // Average over days the user actually SPENT credits (not idle days), so bursty
        // usage isn't diluted across the whole month. e.g. 80 used on 1 day → 80/day.
        const activeDays = new Set(
            thisMonthItems
                .filter(i => i.amount < 0)
                .map(i => new Date(i.createdAt).toDateString())
        ).size;
        return activeDays > 0 ? (usedThisMonth / activeDays).toFixed(1) : '0';
    }, [thisMonthItems, usedThisMonth]);

    // Subscription-based plan monthly allowance (rough guess)
    // Build display packs from API response; fall back to placeholders while loading
    const displayPacks = useMemo(() => {
        if (!Array.isArray(creditProducts) || creditProducts.length === 0) return FALLBACK_PACKS;
        return creditProducts.map((p, i) => {
            const credits = Number(p?.metadata?.credits || 0);
            const usd     = Number(p?.unit_amount || 0) / 100;
            return {
                id:      p.id,
                label:   `${credits.toLocaleString()} credits`,
                price:   `$${usd.toFixed(0)}`,
                per:     credits > 0 ? `$${(usd / credits).toFixed(2)} ea` : '',
                credits,
                best:    i === 1,
            };
        });
    }, [creditProducts]);

    const handleDirectBuy = async () => {
        setCheckoutErrMsg('');
        const pack = displayPacks[selectedPack] ?? displayPacks[0];
        if (!pack?.id) {
            setCheckoutErrMsg('Products are still loading — please wait a moment and try again.');
            return;
        }
        try {
            const result = await dispatch(createCreditCheckoutSession({ priceId: pack.id })).unwrap();
            if (result?.url) { window.location.href = result.url; return; }
            setCheckoutErrMsg('Checkout URL not returned. Please try again.');
        } catch (err) {
            setCheckoutErrMsg(typeof err === 'string' ? err : 'Checkout failed. Please try again.');
        }
    };

    // ── render ────────────────────────────────────────────────────────────────

    const topTypeForBar = Math.max(...Object.values(byType), 1);
    const typeBreakdown = [
        { label: 'Recruiter search',      color: '#E11D48' },
        { label: 'Hiring signal',         color: '#F59E0B' },
        // Job-fetch rows hidden: JSearch flows are retired and ATS fetches are free,
        // so neither accrues new spend — restore if job fetching is ever charged again.
        // { label: 'By My Preferences',     color: '#3F57E0' },
        // { label: 'By Keyword Search',     color: '#8B5CF6' },
        // { label: 'Job search',            color: '#3F57E0' },
        // { label: 'Fetch Latest ATS Jobs', color: '#0EA5E9' },
        { label: 'Feed Auto-Refresh',     color: '#14B8A6' },
    ].map(t => ({ ...t, v: byType[t.label] || 0 })).filter(t => t.v > 0);

    const renewalDate = (() => {
        const end = user?.subscription?.currentPeriodEnd;
        if (!end) return null;
        let d;
        if (end?.$date?.$numberLong) d = new Date(parseInt(end.$date.$numberLong, 10));
        else d = new Date(end);
        if (isNaN(d)) return null;
        return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    })();

    return (
        <div className="h-screen flex flex-col overflow-hidden" style={{ background: '#F6F7FB' }}>
            <div className="fixed top-0 left-0 right-0 z-50">
                <Navbaar />
            </div>

            <div className="flex flex-1 min-h-0 pt-16">
                {/* Sidebar */}
                <div className="hidden sm:flex flex-col w-64 flex-shrink-0 fixed top-16 bottom-0 left-0 z-40 border-r border-[#E6E8EE] bg-white">
                    <Sidebar />
                </div>

                {/* Main content */}
                <div className="flex-1 sm:ml-64 overflow-y-auto">
                    <div className="px-4 pt-5 pb-28">

                        {/* Two-column grid */}
                        <div className="flex gap-4 min-h-0">

                            {/* ── LEFT col ── */}
                            <div className="flex-1 min-w-0 flex flex-col gap-4">

                                {/* Balance hero */}
                                <div
                                    className="rounded-xl p-5 overflow-hidden relative"
                                    style={{
                                        background: 'linear-gradient(135deg, #1F2A56 0%, #3F57E0 70%, #6478F0 100%)',
                                        border: 'none',
                                    }}
                                >
                                    <div className="absolute inset-0 opacity-[.12]" style={{ backgroundImage: 'radial-gradient(circle at 90% 20%, #fff, transparent 50%)' }}/>
                                    <div className="relative flex items-center gap-6 flex-wrap">
                                        {/* Balance */}
                                        <div>
                                            <div className="text-[10px] font-bold uppercase tracking-[.12em] text-white/70 font-dmsans">Current balance</div>
                                            <div className="text-[44px] font-extrabold text-white leading-none mt-1 font-dmsans" style={{ letterSpacing: '-0.03em' }}>
                                                {creditsBalance.toLocaleString()}
                                            </div>
                                            <div className="text-[12px] text-white/80 font-dmsans mt-1.5">
                                                Credits{renewalDate ? ` · resets ${renewalDate}` : ''}
                                            </div>
                                        </div>

                                        {/* Stats */}
                                        <div className="flex-1 grid grid-cols-3 gap-4 min-w-[300px]">
                                            {[
                                                { l: 'Used this month', v: usedThisMonth.toLocaleString(), s: `${creditsBalance.toLocaleString()} remaining` },
                                                { l: 'Avg / day',        v: avgPerDay, s: 'credits' },
                                                { l: 'Top-ups',         v: thisMonthItems.filter(i => i.amount > 0).length.toString(), s: 'this month' },
                                            ].map((s, i) => (
                                                <div key={i} className={`${i > 0 ? 'pl-4 border-l border-white/20' : ''}`}>
                                                    <div className="text-[9.5px] font-semibold uppercase tracking-[.08em] text-white/65 font-dmsans">{s.l}</div>
                                                    <div className="text-[20px] font-bold text-white mt-1 font-dmsans" style={{ letterSpacing: '-0.02em' }}>{s.v}</div>
                                                    <div className="text-[11px] text-white/70 font-dmsans mt-0.5">{s.s}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Filter row */}
                                <div className="flex items-center gap-2 flex-wrap">
                                    {FILTER_TABS.map(tab => (
                                        <button
                                            key={tab}
                                            onClick={() => setActiveFilter(tab)}
                                            className={`px-3 py-1.5 rounded-full text-[12px] font-medium font-dmsans transition-colors ${
                                                activeFilter === tab
                                                    ? 'text-white'
                                                    : 'border border-[#E6E8EE] bg-white text-[#334155] hover:bg-[#F4F5F8]'
                                            }`}
                                            style={activeFilter === tab ? { background: '#3F57E0' } : {}}
                                        >
                                            {tab}
                                            {tab === 'All' && (
                                                <span className="ml-1.5 text-[10px] opacity-70">{filteredHistory.length}</span>
                                            )}
                                        </button>
                                    ))}
                                    <span className="flex-1"/>

                                    {/* Search */}
                                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#E6E8EE] bg-white w-48">
                                        <Search size={12} className="text-[#94A3B8] flex-shrink-0"/>
                                        <input
                                            className="flex-1 text-[12px] font-dmsans outline-none bg-transparent placeholder-[#94A3B8] text-[#0F172A]"
                                            placeholder="Search activity…"
                                            value={searchQuery}
                                            onChange={e => setSearchQuery(e.target.value)}
                                        />
                                    </div>

                                    {/* Time range */}
                                    <div className="flex items-center rounded-lg border border-[#E6E8EE] bg-white overflow-hidden">
                                        {['1d', '7d', '30d', 'All'].map(t => (
                                            <button
                                                key={t}
                                                onClick={() => setTimeRange(t)}
                                                className={`px-3 py-1.5 text-[12px] font-medium font-dmsans transition-colors border-r border-[#E6E8EE] last:border-r-0 ${
                                                    timeRange === t
                                                        ? 'bg-[#3F57E0] text-white font-semibold'
                                                        : 'text-[#334155] hover:bg-[#F4F5F8]'
                                                }`}
                                            >
                                                {t}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Activity log */}
                                <div className="rounded-xl border border-[#E6E8EE] bg-white overflow-hidden">
                                    {/* Card header */}
                                    <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#EFF1F5]">
                                        <div className="text-left">
                                            <div className="text-[13.5px] font-bold text-[#0F172A] font-dmsans">Activity log</div>
                                            <div className="text-[11.5px] text-[#94A3B8] font-dmsans mt-0.5">Newest first · grouped by day</div>
                                        </div>
                                        <button
                                            onClick={() => dispatch(fetchCreditHistory({ limit: 100 }))}
                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E6E8EE] text-[11.5px] font-medium text-[#334155] font-dmsans hover:bg-[#F4F5F8] transition-colors"
                                        >
                                            <RefreshCw size={11}/>
                                            Refresh
                                        </button>
                                    </div>

                                    {/* Loading */}
                                    {creditHistoryLoading && (
                                        <div className="flex flex-col gap-0">
                                            {[1,2,3,4,5,6,7].map(i => (
                                                <div key={i} className="flex items-center gap-3 px-4 py-3 border-b border-[#F4F5F8]">
                                                    <div className="animate-pulse w-7 h-7 rounded-lg bg-[#E9EBF0] flex-shrink-0" />
                                                    <div className="flex flex-col gap-1.5 flex-1">
                                                        <div className="animate-pulse h-3 bg-[#E9EBF0] rounded-lg w-28" />
                                                        <div className="animate-pulse h-2.5 bg-[#E9EBF0] rounded-lg w-44" />
                                                    </div>
                                                    <div className="animate-pulse h-3 bg-[#E9EBF0] rounded-lg w-10 flex-shrink-0" />
                                                    <div className="animate-pulse h-3 bg-[#E9EBF0] rounded-lg w-16 flex-shrink-0" />
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Error */}
                                    {!creditHistoryLoading && creditHistoryError && (
                                        <div className="flex items-center justify-center py-12">
                                            <span className="text-[13px] text-red-500 font-dmsans">{creditHistoryError}</span>
                                        </div>
                                    )}

                                    {/* Empty */}
                                    {!creditHistoryLoading && !creditHistoryError && filteredHistory.length === 0 && (
                                        <div className="flex flex-col items-center justify-center py-14 gap-2">
                                            <div className="w-10 h-10 rounded-full bg-[#EEF1FE] flex items-center justify-center">
                                                <Zap size={18} style={{ color: '#3F57E0' }}/>
                                            </div>
                                            <div className="text-[13px] font-semibold text-[#334155] font-dmsans">No activity yet</div>
                                            <div className="text-[12px] text-[#94A3B8] font-dmsans text-center max-w-[260px]">
                                                {activeFilter !== 'All' ? `No ${activeFilter} activity in this period.` : 'Start using Career Champ features to see your credit usage here.'}
                                            </div>
                                        </div>
                                    )}

                                    {/* Groups */}
                                    {!creditHistoryLoading && grouped.map(([day, rows], gi) => {
                                        const netDay = rows.reduce((s, r) => s + r.amount, 0);
                                        return (
                                            <div key={day}>
                                                {/* Day header */}
                                                <div
                                                    className="flex items-center gap-2 px-5 py-2 border-b border-[#EFF1F5]"
                                                    style={{
                                                        background: '#FAFBFD',
                                                        borderTop: gi > 0 ? '1px solid #EFF1F5' : 'none',
                                                    }}
                                                >
                                                    <Calendar size={11} className="text-[#94A3B8]"/>
                                                    <span className="text-[10.5px] font-bold uppercase tracking-[.08em] text-[#64748B] font-dmsans">{day}</span>
                                                    <span className="flex-1"/>
                                                    <span className="text-[10px] font-semibold text-[#334155] font-dmsans px-2 py-0.5 rounded-full border border-[#E6E8EE] bg-white">
                                                        {netDay >= 0 ? '+' : ''}{netDay} net
                                                    </span>
                                                </div>

                                                {/* Rows */}
                                                {rows.map((item, i) => {
                                                    const type = actionToType(item.action, item.amount);
                                                    const meta = TYPE_META[type] || TYPE_META['Other'];
                                                    const { Icon } = meta;
                                                    const positive = item.amount > 0;

                                                    return (
                                                        <div
                                                            key={item._id || i}
                                                            className="flex items-center gap-3 px-5 py-3.5"
                                                            style={{ borderBottom: i < rows.length - 1 ? '1px solid #EFF1F5' : 'none' }}
                                                        >
                                                            {/* Icon */}
                                                            <div
                                                                className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0 self-center"
                                                                style={{ background: meta.bg, color: meta.color }}
                                                            >
                                                                <Icon size={16}/>
                                                            </div>

                                                            {/* Type + count */}
                                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                                <span className="text-[13px] font-bold text-[#0F172A] font-dmsans">{type}</span>
                                                                <span className="px-1.5 py-0.5 rounded-md bg-[#F4F5F8] text-[10px] font-semibold text-[#64748B] font-dmsans">
                                                                    {getCount(item)}
                                                                </span>
                                                            </div>

                                                            {/* Description — centered */}
                                                            <div className="flex-1 flex items-center justify-center min-w-0 px-2">
                                                                <span className="text-[11.5px] text-[#94A3B8] font-dmsans truncate">{getDescription(item)}</span>
                                                            </div>

                                                            {/* Time + source */}
                                                            <div className="text-right min-w-[120px] self-center">
                                                                <div className="text-[11px] text-[#94A3B8] font-dmsans">{formatDateTime(item.createdAt)}</div>
                                                                {item.source && (
                                                                    <div className="flex items-center gap-1 justify-end mt-1">
                                                                        <span className="text-[10px] text-[#94A3B8] font-dmsans">via</span>
                                                                        <span className="text-[10.5px] font-semibold text-[#64748B]" style={{ fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace' }}>{item.source}</span>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Delta */}
                                                            <div className="text-right min-w-[64px] self-center">
                                                                <div
                                                                    className="text-[15px] font-extrabold font-dmsans"
                                                                    style={{ color: positive ? '#10B981' : '#E11D48', letterSpacing: '-0.01em' }}
                                                                >
                                                                    {positive ? '+' : ''}{item.amount}
                                                                </div>
                                                                {item.balanceAfter != null && (
                                                                    <div className="text-[10.5px] text-[#94A3B8] font-dmsans mt-0.5">balance: {item.balanceAfter}</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}

                                    {/* Footer */}
                                    {!creditHistoryLoading && filteredHistory.length > 0 && (
                                        <div className="flex items-center gap-3 px-5 py-3 border-t border-[#EFF1F5] bg-[#FAFBFD]">
                                            {creditHistoryHasMore && (
                                                <button
                                                    onClick={() => dispatch(fetchCreditHistory({ limit: 100, cursor: creditHistoryNextCursor }))}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E6E8EE] bg-white text-[11.5px] font-medium text-[#334155] font-dmsans hover:bg-[#F4F5F8] transition-colors"
                                                >
                                                    Load more
                                                </button>
                                            )}
                                            <span className="flex-1"/>
                                            <span className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#ECFDF5] text-[10.5px] font-semibold text-[#10B981] font-dmsans">
                                                <CheckCircle size={11}/>
                                                {creditHistoryHasMore ? `${filteredHistory.length}+ loaded` : 'All activity loaded'}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* ── RIGHT sidebar ── */}
                            <div className="w-[300px] flex-shrink-0 flex flex-col gap-4 hidden lg:flex">

                                {/* Usage this month */}
                                <div className="rounded-xl border border-[#E6E8EE] bg-white p-4 text-left">
                                    <div className="text-[10.5px] font-bold uppercase tracking-[.08em] text-[#94A3B8] font-dmsans">Usage this month</div>
                                    <div className="h-px bg-[#EFF1F5] my-2.5"/>

                                    <div className="flex items-baseline gap-1.5 mb-2">
                                        <span className="text-[26px] font-extrabold text-[#0F172A] font-dmsans" style={{ letterSpacing: '-0.02em' }}>{usedThisMonth.toLocaleString()}</span>
                                        <span className="text-[12px] text-[#94A3B8] font-dmsans">used · {creditsBalance.toLocaleString()} remaining</span>
                                    </div>

                                    {/* Progress bar — share of total (used + remaining) consumed this month */}
                                    <div className="h-1.5 rounded-full bg-[#EEF1F5] overflow-hidden mb-1.5">
                                        <div
                                            className="h-1.5 rounded-full transition-all"
                                            style={{ width: `${Math.min((usedThisMonth / Math.max(1, usedThisMonth + creditsBalance)) * 100, 100)}%`, background: '#3F57E0' }}
                                        />
                                    </div>
                                    <div className="text-[10.5px] text-[#94A3B8] font-dmsans mb-3">
                                        {usedThisMonth === 0
                                            ? 'No usage yet this month'
                                            : `~${(usedThisMonth / getDaysIntoMonth() * 30).toFixed(0)} credits on pace for month`}
                                    </div>

                                    <div className="h-px bg-[#EFF1F5] my-2.5"/>

                                    {/* Per-type breakdown — follows the 7d/30d/All selector */}
                                    <div className="text-[10px] font-bold uppercase tracking-[.08em] text-[#94A3B8] font-dmsans mb-2">
                                        By type · {({ '1d': 'last 24 hours', '7d': 'last 7 days', '30d': 'last 30 days', 'All': 'all time' })[timeRange]}
                                    </div>
                                    {typeBreakdown.length === 0 ? (
                                        <div className="text-[11.5px] text-[#94A3B8] font-dmsans py-2">No deductions in this period</div>
                                    ) : (
                                        <div className="space-y-2.5">
                                            {typeBreakdown.map((t, i) => (
                                                <div key={i}>
                                                    <div className="flex items-center justify-between mb-1.5">
                                                        <div className="flex items-center gap-1.5">
                                                            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: t.color }}/>
                                                            <span className="text-[11.5px] font-semibold text-[#334155] font-dmsans">{t.label}</span>
                                                        </div>
                                                        <span className="text-[12px] font-bold text-[#0F172A] font-dmsans">{t.v}</span>
                                                    </div>
                                                    <div className="h-1.5 rounded-full bg-[#EEF1F5] overflow-hidden">
                                                        <div style={{ width: `${(t.v / topTypeForBar) * 100}%`, height: '100%', background: t.color, borderRadius: 999 }}/>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Buy credits mini-picker */}
                                <div
                                    className="rounded-xl border p-4 text-left"
                                    style={{ background: 'linear-gradient(180deg, #EEF1FE 0%, #fff 70%)', borderColor: '#C7D0FA' }}
                                >
                                    <div className="flex items-center gap-2.5 mb-3">
                                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#3F57E0' }}>
                                            <Zap size={14} className="text-white"/>
                                        </div>
                                        <div>
                                            <div className="text-[13px] font-bold text-[#0F172A] font-dmsans">Need more credits?</div>
                                            <div className="text-[11px] text-[#94A3B8] font-dmsans">Top-ups never expire</div>
                                        </div>
                                    </div>

                                    <div className="h-px bg-[#E6E8EE] mb-3"/>

                                    <div className="space-y-2">
                                        {displayPacks.map((pack, i) => (
                                            <button
                                                key={i}
                                                onClick={() => setSelectedPack(i)}
                                                className="w-full text-left relative"
                                            >
                                                {pack.best && (
                                                    <span className="absolute -top-1.5 right-2 text-[8.5px] font-bold text-white px-1.5 py-0.5 rounded-full" style={{ background: '#3F57E0', letterSpacing: '.05em' }}>BEST VALUE</span>
                                                )}
                                                <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition-all ${
                                                    selectedPack === i
                                                        ? 'border-[#3F57E0] bg-[#EEF1FE] shadow-sm'
                                                        : 'border-[#E6E8EE] bg-transparent hover:bg-white/60'
                                                }`}>
                                                    <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedPack === i ? 'border-[#3F57E0]' : 'border-[#D1D5DB]'}`}>
                                                        {selectedPack === i && <div className="w-1.5 h-1.5 rounded-full bg-[#3F57E0]"/>}
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="text-[12.5px] font-bold text-[#0F172A] font-dmsans">{pack.label}</div>
                                                        <div className="text-[10.5px] text-[#94A3B8] font-dmsans">{pack.per}</div>
                                                    </div>
                                                    <div className="text-[13px] font-extrabold font-dmsans" style={{ color: '#3F57E0' }}>{pack.price}</div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>

                                    <button
                                        onClick={handleDirectBuy}
                                        disabled={checkoutLoading}
                                        className="mt-3 w-full py-2 rounded-xl text-[12.5px] font-bold text-white font-dmsans flex items-center justify-center gap-1.5 transition-opacity disabled:opacity-50"
                                        style={{ background: '#3F57E0' }}
                                    >
                                        {checkoutLoading ? 'Redirecting…' : <>Pay {(displayPacks[selectedPack] ?? displayPacks[0])?.price} <ArrowRight size={12}/></>}
                                    </button>
                                    {checkoutErrMsg && (
                                        <p className="text-[10.5px] text-red-500 font-dmsans mt-1">{checkoutErrMsg}</p>
                                    )}
                                    <div className="mt-2 text-center">
                                        <span className="text-[10.5px] text-[#94A3B8] font-dmsans">Secured by Stripe</span>
                                    </div>
                                </div>

                                {/* How credits are spent */}
                                <div className="rounded-xl border border-[#E6E8EE] bg-white p-4 text-left">
                                    <div className="text-[10.5px] font-bold uppercase tracking-[.08em] text-[#94A3B8] font-dmsans">How credits are spent</div>
                                    <div className="h-px bg-[#EFF1F5] my-2.5"/>
                                    <div className="space-y-0">
                                        {COST_REFERENCE.map((r, i) => (
                                            <div
                                                key={i}
                                                className="flex items-center gap-2.5 py-2"
                                                style={{ borderBottom: i < COST_REFERENCE.length - 1 ? '1px solid #EFF1F5' : 'none' }}
                                            >
                                                <div className="flex-1 text-[12px] font-semibold text-[#334155] font-dmsans">{r.label}</div>
                                                <div className="text-[11.5px] text-[#64748B] font-semibold font-dmsans text-right">{r.cost}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default CreditsPage;
